import type { RunResult } from "../../domain/models";

export const resultMessageFor = (
  endReason: RunResult["endReason"],
): { heading: string; reason: string } => {
  if (endReason === "target_complete") {
    return {
      heading: "Level cleared",
      reason: "You completed the level objective before the timer reached 0.0 seconds.",
    };
  }
  if (endReason === "hazard_timeout") {
    return {
      heading: "Security caught you",
      reason: "A hazard hit removed your remaining time, so the run ended.",
    };
  }
  return {
    heading: "Time expired",
    reason: "The countdown reached 0.0 seconds before the objective was completed.",
  };
};

export const resultOutcomeFor = (
  result: RunResult,
): { label: string; title: string; body: string; tone: "success" | "danger" | "neutral" } => {
  if (result.endReason === "target_complete") {
    return {
      label: "Completed",
      title: "Objective complete",
      body: "The vault target was cleared in time. You can move to the next level.",
      tone: "success",
    };
  }

  if (result.endReason === "hazard_timeout") {
    return {
      label: "Run over",
      title: "Ended by hazard penalty",
      body: "Security damage emptied the timer. Replay the level and avoid hazard contact.",
      tone: "danger",
    };
  }

  return {
    label: "Run over",
    title: "Ended by timer",
    body: "The timer reached zero. Collect faster, build combos, and grab Time Diamonds to survive longer.",
    tone: "neutral",
  };
};

export const runTransitionMessageFor = (
  endReason: NonNullable<RunResult["endReason"]>,
  objectiveLabel?: string,
): { title: string; detail: string } => {
  if (endReason === "target_complete") {
    return {
      title: "Target complete",
      detail: `${objectiveLabel ?? "Objective completed"}. Level cleared.`,
    };
  }
  if (endReason === "hazard_timeout") {
    return {
      title: "Security hit",
      detail: "A hazard penalty emptied your timer. Run over.",
    };
  }
  return {
    title: "Time up",
    detail: "The timer reached 0.0 seconds before the objective was completed. Run over.",
  };
};

export class ResultsScreen {
  constructor(private readonly root: HTMLElement) {}

  show(result: RunResult, vaultName: string): void {
    const { heading, reason } = resultMessageFor(result.endReason);
    const outcome = resultOutcomeFor(result);
    this.setText(
      "result-kicker",
      result.endReason !== "target_complete"
        ? "Run over"
        : result.isHighScore ? "New high score" : `${vaultName} secured`,
    );
    this.setText("result-heading", heading);
    this.setText("result-reason", reason);
    this.setText("result-score", result.score.toLocaleString());
    this.setText("result-diamonds", String(result.diamonds));
    this.setText("result-xp", `+${result.xp}`);
    this.setTextIfPresent("result-gold", `+${result.gold}`);
    this.setTextIfPresent("result-primary-gold", `+${result.gold}`);
    this.setTextIfPresent("result-outcome-label", outcome.label);
    this.setTextIfPresent("result-outcome-title", outcome.title);
    this.setTextIfPresent("result-outcome-body", outcome.body);
    const outcomeRoot = this.root.querySelector<HTMLElement>("#result-outcome");
    if (outcomeRoot) outcomeRoot.dataset.tone = outcome.tone;
  }

  private setText(id: string, value: string): void {
    const element = this.root.querySelector<HTMLElement>(`#${id}`);
    if (!element) throw new Error(`Missing results element #${id}`);
    element.textContent = value;
  }

  private setTextIfPresent(id: string, value: string): void {
    const element = this.root.querySelector<HTMLElement>(`#${id}`);
    if (element) element.textContent = value;
  }
}